import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import StagedPremiumSettingsWizard from '../../components/PremiumSettings/stagedPremiumSettingsWizard'
import { useState } from 'react'
import {
    changeLawsysFirmState,
    requestLawsysFirmsFiltered,
} from '../../actions/lawsysFirms'
import { stringToNumber, hasTooManyDecimals, validateAmount, roundToTwoDecimals } from '../../helpers/functions'
import {
    changeSubSettings,
    changeWizardData,
    getCoverageTypes,
    resetApplicationStagedPremium,
} from '../../reducers/stagedPremiumsWizard'
import CONTENT_STRINGS from '../../helpers/contentStrings'

const StagedPremiumSettingsWizardHOC = (props) => {
    const {
        handleSubmit,
        onCancel,
        isEdit,
        isEditStaged,
        onEditStaged,
        isApplicationPremium,
        isChangingStatusOfApp,
    } = props;
    const [currentStep, setStep] = useState(1);
    const [commissionModal, toggleCommissionModal] = useState(false);
    const dispatch = useDispatch();
    const changeData = (key, value) => {dispatch(changeWizardData({ key, value }))};
    const caseTypes = useSelector((state) => state.caseTypes);
    const lawsysFirms = useSelector((state) => state.lawsysFirms);
    const applicationPolicy = useSelector((state) => state.applicationPolicy);
    const stagedPremiumSettingsWizard = useSelector((state) => state.stagedPremiumsWizard);
    const stagedSubSettings = Object.values(useSelector((state) => state.stagedPremiumsWizard.stagesSubSettings));
    const agentsList = Object.values(useSelector((state) => state.stagedPremiumsWizard.agentCommissionsList));
    const premiumSettingsWizard = useSelector((state) => state.premiumSettingsWizard);
    const premiumSettings = useSelector((state) => state.premiumSettings);
    const caseTypeName = caseTypes?.cases.filter((c) => c.id == premiumSettings.selectedCaseType)[0]?.name;
    const subCaseTypeName = caseTypes?.subCases.filter((c) => c.id == premiumSettings.selectedSubCaseType)[0]?.name;
    const workSourceName = premiumSettings?.worksources.filter((c) => c.id == premiumSettings.selectedWorksource);
    const stagedPremiums = useSelector((state) => state.stagedPremiums)
    const appStagedSubSettings = Object.values(useSelector((state) => state.stagedPremiums.stagesSubSettings));
    const formIsFetching =
        stagedPremiumSettingsWizard.fetching ||
        stagedPremiumSettingsWizard.fetchingStagedPremium ||
        stagedPremiumSettingsWizard.fetchingCoverageTypes ||
        stagedPremiumSettingsWizard.fetchingStages

    useEffect(() => {
        dispatch(getCoverageTypes())
    }, []);

    useEffect(() => {
        if (lawsysFirms.suggestionFieldValue.length >= 3) {
        const askServer = setTimeout(() => {
            dispatch(
            requestLawsysFirmsFiltered({
                search: lawsysFirms.suggestionFieldValue,
            })
            )
        }, 500)
        return () => clearTimeout(askServer)
        }
    }, [lawsysFirms.suggestionFieldValue]);

    const handleNextStep = () => {
        var validationError = ''
        changeData('errorResponse', '')
        if (currentStep === 2) {
            // Amount fields — real money, so max 2 decimals, matching the backend's
            // DecimalFormatter.Round(x) default and the keystroke-level cap in this form.
            const amountFieldValues = [
                stagedPremiumSettingsWizard.averageExpectedPremium,
                ...stagedSubSettings.flatMap(s => [s.premiumAmount, s.limitOfIndemnity]),
            ];
            // Percentage fields — backend always stores these at 4dp (DecimalFormatter.Round(x, 4)),
            // so validate against that ceiling here even though typing itself is capped at 2dp.
            const percentageFieldValues = [
                stagedPremiumSettingsWizard.underwriterRatePercentage,
                ...agentsList.map(a => a.commissionPercentage),
            ];
            const decimalErr = [
                ...amountFieldValues.filter(v => v !== '' && v !== null && v !== undefined).map(v => validateAmount(v, 0, 2)),
                ...percentageFieldValues.filter(v => v !== '' && v !== null && v !== undefined).map(v => validateAmount(v, 0, 4)),
            ].find(err => err !== '');
            if (decimalErr) {
                changeData('errorResponse', decimalErr);
                return;
            }
        }
        const steps = [
            {
                step: 2,
                errorMessage: CONTENT_STRINGS.STAGED_PREMIUMS_ERROR,
                check: () =>
                stagedSubSettings.some(
                    ({ limitOfIndemnity, premiumAmount }, index) =>
                    index > 0 &&
                    (parseFloat(limitOfIndemnity) <
                        parseFloat(stagedSubSettings[index - 1].limitOfIndemnity) ||
                        parseFloat(premiumAmount) <
                        parseFloat(stagedSubSettings[index - 1].premiumAmount))
                ),
            },
            {
                step: 2,
                errorMessage: CONTENT_STRINGS.STAGED_PREMIUMS_AMOUNT_ERROR,
                check: () =>
                stagedSubSettings.some(
                    ({ limitOfIndemnity, premiumAmount }) =>
                    !limitOfIndemnity ||
                    !premiumAmount ||
                    limitOfIndemnity < 1 ||
                    premiumAmount < 1
                ),
            },
            {
                step: 2,
                errorMessage: CONTENT_STRINGS.RATES_REQUIRED,
                check: () =>
                    agentsList.some(
                        ({ commissionPercentage }) => !commissionPercentage
                    ) || !stagedPremiumSettingsWizard.underwriterRatePercentage,
            },
            {
                step: 2,
                errorMessage: CONTENT_STRINGS.STAGED_PREMIUMS_SUM_ERROR,
                check: () => {
                    const commissionSum = agentsList.reduce(
                        (sum, agent) =>
                        parseFloat(sum) +
                        parseFloat(agent.commissionPercentage.toString()),
                        0
                    )
                    return (
                        commissionSum + parseFloat( stagedPremiumSettingsWizard.underwriterRatePercentage.toString() ) > 100
                    )
                },
            },
            {
                step: 3,
                errorMessage: CONTENT_STRINGS.REQUIRED_FIELDS,
                check: () =>
                    !stagedPremiumSettingsWizard.selectedCoverageType ||
                    !stagedPremiumSettingsWizard.selectedDeductible ||
                    !stagedPremiumSettingsWizard.selectedEndorsement ||
                    !stagedPremiumSettingsWizard.validFrom
            },
            {
                step: 3,
                errorMessage: CONTENT_STRINGS.ERROR_VALID_DATE,
                check: () =>
                stagedPremiumSettingsWizard.validFrom &&
                stagedPremiumSettingsWizard.validUntil &&
                new Date(stagedPremiumSettingsWizard.validFrom) >=
                    new Date(stagedPremiumSettingsWizard.validUntil),
            },
        ]
        validationError = steps.find((s) => s.step === currentStep && s.check())?.errorMessage
        if (validationError) {
            changeData('errorResponse', validationError)
        } else {
            changeData('errorResponse', validationError)
            setStep(currentStep + 1)
        }
    }

    const onSuggestionSelected = (value) => {
        let lawsysFirm = lawsysFirms.lawsysFirmsFiltered.find( (x) => x.id === value.value )
        dispatch(changeLawsysFirmState('lawsysFirmsFiltered', [lawsysFirm]))
        dispatch(changeLawsysFirmState('selectedLawsysFirmsDropdown', [lawsysFirm]))
        dispatch(
            changeLawsysFirmState('selectedLawsysFirms', [
                ...lawsysFirms.selectedLawsysFirms,
                lawsysFirm,
            ])
        )
        changeData('selectedAgent', value.value)
    }

    const onSuggestionChange = (value, action) => {
        if (action.action !== 'input-blur' && action.action !== 'menu-close' ) {
            if (value.length < 3) {
                dispatch(changeLawsysFirmState('lawsysFirmsFiltered', []))
                dispatch(changeLawsysFirmState('selectedLawsysFirmsDropdown', []))
                changeData('selectedAgent', '')
            }
            dispatch(changeLawsysFirmState('suggestionFieldValue', value))
        }
    }

    const addCommission = () => {
        const selectedFirm = lawsysFirms.selectedLawsysFirmsDropdown[0]
        if (!selectedFirm) return;
        let solicitorsList = [...agentsList]
        const agentDetails = {
            id: selectedFirm.id,
            name: selectedFirm.name,
            aragProvider: selectedFirm.aragProvider,
            commission: '',
            commissionPercentage: '',
        }
        solicitorsList.push(agentDetails)

        const newList = solicitorsList.reduce((values, agent) => {
            values[agent.id] = agent
            return values
        }, {})

        changeData('agentCommissionsList', newList)
        toggleCommissionModal(!commissionModal)
        dispatch(changeLawsysFirmState('suggestionFieldValue', ''))
        dispatch(changeLawsysFirmState('lawsysFirmsFiltered', []))
        dispatch(changeLawsysFirmState('selectedLawsysFirmsDropdown', []))
        changeData('selectedAgent', '')
    }

    //function that removes a solicitor from the list
    const removeSolicitor = (solicitorId) => {
        const solicitorsList = agentsList.filter((c) => c.id != solicitorId)
        const newList = solicitorsList.reduce((values, agent) => {
            values[agent.id] = agent
            return values
        }, {})

        changeData('agentCommissionsList', newList)
    }

    const amountColumnsDisplayDataPerStage = (stage) => {
        const premiumAmount = stringToNumber(stage.premiumAmount);
        // Commission is a money amount derived from a percentage — round to 2dp (money),
        // not the 4dp used for the percentage it's derived from.
        const commissionsAgentsData = agentsList.map((a) => ({
            title: a.name,
            value: roundToTwoDecimals(stringToNumber(a.commissionPercentage) * 0.01 * premiumAmount),
            isPlain: true,
            onChange: () => null,
            isCommissionAgent: true,
            aragProvider: a.aragProvider,
        }))
        return [
            {
                title: CONTENT_STRINGS.PREMIUM,
                value: stage.premiumAmount,
                isPlain: false,
                onChange: (e) => {
                    if (!hasTooManyDecimals(e.target.value))
                        dispatch(changeSubSettings({ target: stage.stageMachineName, key: 'premiumAmount', value: e.target.value }));
                },
            },
            {
                title: CONTENT_STRINGS.LIMIT_OF_INDEMNITY,
                value: stage.limitOfIndemnity,
                isPlain: false,
                onChange: (e) => {
                    if (!hasTooManyDecimals(e.target.value))
                        dispatch(changeSubSettings({ target: stage.stageMachineName, key: 'limitOfIndemnity', value: e.target.value }));
                },
            },
            {
                title: CONTENT_STRINGS.UNDERWRITER_RATE,
                // Underwriter rate is likewise a derived money amount — 2dp, not 4dp.
                value: roundToTwoDecimals(stringToNumber(stagedPremiumSettingsWizard.underwriterRatePercentage) * 0.01 * premiumAmount),
                isPlain: true,
                onChange: () => null,
            },
            ...commissionsAgentsData,
        ]
    }
    return (
        <StagedPremiumSettingsWizard
            nextStep={handleNextStep}
            onEditStaged={onEditStaged}
            isEditStaged={isEditStaged}
            appStagedSubSettings={appStagedSubSettings}
            applicationPolicy={applicationPolicy}
            stagedPremiums={stagedPremiums}
            isChangingStatusOfApp={isChangingStatusOfApp}
            previousStep={() => {
                changeData('errorResponse', '')
                setStep(currentStep - 1)
            }}
            formIsFetching={formIsFetching}
            stagedSubSettings={stagedSubSettings}
            agentsList={agentsList}
            displayDataPerStage={amountColumnsDisplayDataPerStage}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionChange={onSuggestionChange}
            addCommission={addCommission}
            removeSolicitor={removeSolicitor}
            lawsysFirms={lawsysFirms}
            currentStep={currentStep}
            caseTypeName={caseTypeName}
            subCaseTypeName={subCaseTypeName}
            stagedPremiumSettingsWizard={stagedPremiumSettingsWizard}
            premiumSettingsWizard={premiumSettingsWizard}
            premiumSettings={premiumSettings}
            changeStageSubSetting={(target, key, value) =>
                dispatch(changeSubSettings({ target, key, value }))
            }
            changeStagedPremiumFormData={(key, value) => changeData(key, value)}
            handleSubmit={handleSubmit}
            onCancel={() => {
                dispatch(resetApplicationStagedPremium());
                changeData('errorResponse', '');
                setStep(1);
                onCancel();
            }}
            isEdit={isEdit}
            isApplicationPremium={isApplicationPremium}
            workSourceName={workSourceName[0]?.name}
            commissionModal={commissionModal}
            toggleCommissionModal={() => toggleCommissionModal(!commissionModal)}
        />
    )
}
export default StagedPremiumSettingsWizardHOC
